import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { AnchorIcon } from '@/components/ui/anchor-icon';
import { cn } from '@/lib/utils';
import { Send, Wrench, Folder } from 'lucide-react';
import { TOOL_ICONS } from '@/lib/tool-icons';

export default function HomePromptBox({ projects = [], onSubmit, loading = false }) {
  const [promptValue, setPromptValue] = useState('');
  const [selectedTools, setSelectedTools] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toolsDropdownRef = useRef(null);
  const projectsDropdownRef = useRef(null);
  const toolsPillRef = useRef(null);
  const projectsPillRef = useRef(null);

  // Handle clicking outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown === 'tools') {
        if (
          toolsDropdownRef.current &&
          !toolsDropdownRef.current.contains(event.target) &&
          toolsPillRef.current &&
          !toolsPillRef.current.contains(event.target)
        ) {
          setActiveDropdown(null);
        }
      } else if (activeDropdown === 'projects') {
        if (
          projectsDropdownRef.current &&
          !projectsDropdownRef.current.contains(event.target) &&
          projectsPillRef.current &&
          !projectsPillRef.current.contains(event.target)
        ) {
          setActiveDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Toggle tool selection
  const toggleTool = useCallback((toolId) => {
    setSelectedTools((prev) =>
      prev.includes(toolId)
        ? prev.filter((id) => id !== toolId)
        : [...prev, toolId]
    );
  }, []);

  // Toggle project selection
  const toggleProject = useCallback((projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (!promptValue.trim() || loading) return;

    onSubmit({
      query: promptValue,
      selectedTools,
      selectedProjects
    });

    // Clear input after submission
    setPromptValue('');
  }, [promptValue, selectedTools, selectedProjects, onSubmit, loading]);

  // Toggle dropdown visibility
  const toggleDropdown = useCallback((dropdown) => {
    setActiveDropdown((prev) => (prev === dropdown ? null : dropdown));
  }, []);

  return (
    <div className="glass-elevated rounded-[24px] p-5 relative">
      {/* Input row */}
      <div className="relative">
        <AnchorIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
        <input
          type="text"
          value={promptValue}
          onChange={(e) => setPromptValue(e.target.value)}
          placeholder="Ask anything..."
          disabled={loading}
          className="w-full h-14 pl-12 pr-4 rounded-xl bg-transparent focus:outline-none text-base placeholder:text-muted-foreground"
        />
      </div>

      {/* Filter pills and Ask button row */}
      <div className="pt-3 mt-3 border-t border-black/[0.08] dark:border-white/[0.08] flex items-center justify-between">
        {/* Left side - Filter pills */}
        <div className="flex items-center gap-2">
          {/* Tools pill button */}
          <button
            ref={toolsPillRef}
            onClick={() => toggleDropdown('tools')}
            className={cn(
              "flex items-center gap-1.5 px-3 h-8 rounded-full border transition-colors",
              activeDropdown === 'tools'
                ? "bg-primary/10 border-primary text-primary"
                : "border-black/[0.08] dark:border-white/[0.08] hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
            )}
          >
            <Wrench className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">Tools</span>
            {selectedTools.length > 0 && (
              <span className="ml-0.5 flex items-center justify-center h-4 w-4 rounded-full bg-primary text-white text-xs font-semibold">
                {selectedTools.length}
              </span>
            )}
          </button>

          {/* Projects pill button */}
          <button
            ref={projectsPillRef}
            onClick={() => toggleDropdown('projects')}
            className={cn(
              "flex items-center gap-1.5 px-3 h-8 rounded-full border transition-colors",
              activeDropdown === 'projects'
                ? "bg-primary/10 border-primary text-primary"
                : "border-black/[0.08] dark:border-white/[0.08] hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
            )}
          >
            <Folder className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">Projects</span>
            {selectedProjects.length > 0 && (
              <span className="ml-0.5 flex items-center justify-center h-4 w-4 rounded-full bg-primary text-white text-xs font-semibold">
                {selectedProjects.length}
              </span>
            )}
          </button>
        </div>

        {/* Right side - Ask button */}
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!promptValue.trim() || loading}
          className="rounded-full px-5 gap-1.5 h-8 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
        >
          <Send className="h-3.5 w-3.5" />
          <span>Ask</span>
        </Button>
      </div>

      {/* Tools dropdown */}
      {activeDropdown === 'tools' && (
        <div
          ref={toolsDropdownRef}
          className="absolute top-full mt-2 left-0 w-44 rounded-2xl border border-black/[0.08] dark:border-white/[0.08] shadow-xl p-1.5 z-50 bg-white dark:bg-neutral-800"
        >
          {TOOL_ICONS.map((tool) => {
            const ToolIconComponent = tool.icon;
            const isSelected = selectedTools.includes(tool.id);

            return (
              <button
                key={tool.id}
                onClick={() => toggleTool(tool.id)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors text-left",
                  isSelected
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-black/[0.04] dark:hover:bg-white/[0.08]"
                )}
              >
                <ToolIconComponent className="h-4 w-4" />
                <span className="text-sm font-medium">{tool.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Projects dropdown */}
      {activeDropdown === 'projects' && (
        <div
          ref={projectsDropdownRef}
          className="absolute top-full mt-2 left-0 w-44 rounded-2xl border border-black/[0.08] dark:border-white/[0.08] shadow-xl p-1.5 z-50 bg-white dark:bg-neutral-800"
        >
          {projects.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No projects available
            </div>
          ) : (
            projects.map((project) => {
              const isSelected = selectedProjects.includes(project.id);

              return (
                <button
                  key={project.id}
                  onClick={() => toggleProject(project.id)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors text-left",
                    isSelected
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-black/[0.04] dark:hover:bg-white/[0.08]"
                  )}
                >
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <span className="text-sm font-medium truncate">{project.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
